import { Injectable, signal } from '@angular/core';
import { FaTask, FaTaskPriority, FaTaskStatus, FaTasksState, FaSubtask } from './fa-tasks.model';

function uuid(): string {
  return Math.random().toString(36).slice(2, 10) + '-' + Date.now().toString(36);
}

function subUuid(): string {
  return Math.random().toString(36).slice(2, 8);
}

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function daysFromNow(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function seedTasks(): FaTask[] {
  return [
    {
      id: uuid(),
      taskName: 'Prepare IRA rollover paperwork',
      description: 'Gather transfer forms from prior 401(k) provider and initiate rollover to client IRA.',
      opportunityId: null,
      assignee: 'Sarah Chen',
      priority: 'P1',
      status: 'In Progress',
      labels: ['IRA', 'Paperwork'],
      subtasks: [
        { id: subUuid(), title: 'Request transfer forms from prior custodian', done: true },
        { id: subUuid(), title: 'Complete client signature pages', done: true },
        { id: subUuid(), title: 'Submit forms to receiving custodian', done: false },
        { id: subUuid(), title: 'Confirm transfer completion', done: false },
      ],
      recurrence: 'none',
      createdDate: '2025-11-01',
      updatedDate: '2025-11-10',
      dueDate: daysFromNow(5),
      plannedDate: daysFromNow(2),
    },
    {
      id: uuid(),
      taskName: 'Review life insurance coverage gap',
      description: 'Analyze current term life coverage vs. client income replacement needs. Prepare recommendation memo.',
      opportunityId: null,
      assignee: 'Sarah Chen',
      priority: 'P2',
      status: 'To Do',
      labels: ['Insurance', 'Review'],
      subtasks: [
        { id: subUuid(), title: 'Pull current policy details', done: false },
        { id: subUuid(), title: 'Run coverage gap analysis', done: false },
        { id: subUuid(), title: 'Draft recommendation memo', done: false },
      ],
      recurrence: 'none',
      createdDate: '2025-10-15',
      updatedDate: '2025-10-15',
      dueDate: daysFromNow(14),
      plannedDate: daysFromNow(7),
    },
    {
      id: uuid(),
      taskName: 'Send quarterly portfolio summary',
      description: 'Compile Q4 performance report and send to client with commentary on allocation changes.',
      opportunityId: null,
      assignee: 'Marcus Webb',
      priority: 'P2',
      status: 'To Do',
      labels: ['Reporting', 'Quarterly'],
      subtasks: [
        { id: subUuid(), title: 'Pull Q4 performance data', done: false },
        { id: subUuid(), title: 'Write commentary', done: false },
        { id: subUuid(), title: 'Send to client', done: false },
      ],
      recurrence: 'custom',
      recurrenceCustomText: 'Every quarter end',
      createdDate: '2025-11-05',
      updatedDate: '2025-11-05',
      dueDate: daysFromNow(21),
      plannedDate: daysFromNow(14),
    },
    {
      id: uuid(),
      taskName: 'Schedule annual review meeting',
      description: 'Coordinate with client to book annual financial plan review. Confirm agenda 1 week prior.',
      opportunityId: null,
      assignee: 'Sarah Chen',
      priority: 'P3',
      status: 'Blocked',
      labels: ['Meeting', 'Annual Review'],
      subtasks: [
        { id: subUuid(), title: 'Send availability options to client', done: true },
        { id: subUuid(), title: 'Confirm meeting slot', done: false },
        { id: subUuid(), title: 'Send calendar invite', done: false },
        { id: subUuid(), title: 'Prepare agenda', done: false },
        { id: subUuid(), title: 'Send pre-meeting materials', done: false },
      ],
      recurrence: 'custom',
      recurrenceCustomText: 'Annually',
      createdDate: '2025-09-20',
      updatedDate: '2025-10-01',
      dueDate: daysFromNow(-3),
      plannedDate: daysFromNow(-7),
    },
  ];
}

function emptyState(): FaTasksState {
  return { tasks: seedTasks() };
}

/**
 * Scoped state service for FA Tasks.
 * Provided at ClientActionsComponent level (not root) so state is
 * isolated per component instance and cleared on navigation away.
 */
@Injectable()
export class FaTasksStateService {
  private _store = signal<Record<string, FaTasksState>>({});

  /** Read-only snapshot of the store for reactive computed() bindings in components. */
  readonly storeSnapshot = this._store.asReadonly();

  /**
   * Returns state for the given session. If no state exists yet, seeds it.
   * NOTE: Do not call from inside a computed() — use ensureState() first then read storeSnapshot.
   */
  getState(sessionId: string): FaTasksState {
    const store = this._store();
    return store[sessionId] ?? this._seed(sessionId);
  }

  /** Ensures state exists for the session without returning it (safe to call from effects). */
  ensureState(sessionId: string): void {
    if (!this._store()[sessionId]) {
      this._seed(sessionId);
    }
  }

  private _seed(sessionId: string): FaTasksState {
    const seeded = emptyState();
    this._store.update((s) => ({ ...s, [sessionId]: seeded }));
    return seeded;
  }

  private _updateTask(sessionId: string, id: string, patch: Partial<FaTask>): void {
    this._store.update((store) => {
      const state = store[sessionId] ?? emptyState();
      return {
        ...store,
        [sessionId]: {
          ...state,
          tasks: state.tasks.map((t) =>
            t.id === id
              ? { ...t, ...patch, updatedDate: today() }
              : t
          ),
        },
      };
    });
  }

  updateTaskStatus(sessionId: string, id: string, status: FaTaskStatus): void {
    this._updateTask(sessionId, id, { status });
  }

  updateTaskPriority(sessionId: string, id: string, priority: FaTaskPriority): void {
    this._updateTask(sessionId, id, { priority });
  }

  updateTask(sessionId: string, id: string, patch: Partial<FaTask>): void {
    this._updateTask(sessionId, id, patch);
  }

  updateSubtask(sessionId: string, taskId: string, subtaskId: string, done: boolean): void {
    this._store.update((store) => {
      const state = store[sessionId] ?? emptyState();
      return {
        ...store,
        [sessionId]: {
          ...state,
          tasks: state.tasks.map((t) => {
            if (t.id !== taskId) return t;
            return {
              ...t,
              updatedDate: today(),
              subtasks: t.subtasks.map((s: FaSubtask) =>
                s.id === subtaskId ? { ...s, done } : s
              ),
            };
          }),
        },
      };
    });
  }

  addTask(sessionId: string, task: Omit<FaTask, 'id'>): void {
    const newTask: FaTask = { ...task, id: uuid() };
    this._store.update((store) => {
      const state = store[sessionId] ?? emptyState();
      return {
        ...store,
        [sessionId]: {
          ...state,
          tasks: [...state.tasks, newTask],
        },
      };
    });
  }
}
